import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { BACKGROUND_PARAMS } from '../background';
import { TEXT_LAYER_PARAMS } from '../params/textLayer';
import { defaultValues, withDefaults, coerceValue, type ParamSchema, type ParamValues } from '../params/types';
import { normalisePostChain } from '../postfx/chain';
import { TEXT_SCHEMA } from '../text/textParams';
import { getTechnique } from '../../techniques/registry';
import { TEXT_TECHNIQUE_ID, createLayerId, type BlendMode, type Layer } from '../../store/layer';
import type { StudioDocument } from '../../store/studioStore';

/**
 * Preset serialisation.
 *
 * The document is already the single serialisable shape, so a preset *is* the document
 * plus a version tag. What matters is the way back in: values are coerced against the
 * *current* schemas, so a preset saved before a parameter was added, removed or had its
 * range narrowed still loads, with the missing pieces filled from defaults rather than
 * producing a broken composition or a crash.
 */
export const PRESET_VERSION = 1;

export interface PresetFile {
  format: 'typo-studio-preset';
  version: number;
  name: string;
  createdAt: string;
  doc: StudioDocument;
}

export function serialisePreset(doc: StudioDocument, name: string): PresetFile {
  return {
    format: 'typo-studio-preset',
    version: PRESET_VERSION,
    name,
    createdAt: new Date().toISOString(),
    // structuredClone keeps typed values (palette objects, curves) intact and detaches
    // the preset from the live document.
    doc: structuredClone(doc),
  };
}

function sanitise(schema: ParamSchema, values: ParamValues | undefined): ParamValues {
  const filled = withDefaults(schema, values ?? {});
  const out: ParamValues = {};
  for (const p of schema) out[p.key] = coerceValue(p, filled[p.key]);
  return out;
}

const BLEND_MODES = new Set<BlendMode>([
  'normal',
  'multiply',
  'screen',
  'overlay',
  'difference',
  'exclusion',
  'lighten',
  'darken',
  'color-dodge',
  'hard-light',
]);

function sanitiseLayer(raw: Partial<Layer> | undefined): Layer | null {
  if (!raw || typeof raw.techniqueId !== 'string') return null;

  const isText = raw.techniqueId === TEXT_TECHNIQUE_ID;
  const technique = isText ? null : getTechnique(raw.techniqueId);
  // A preset referencing a technique this build does not have is dropped rather than
  // rendered as an empty layer that silently does nothing.
  if (!isText && !technique) return null;

  const schema = isText ? TEXT_LAYER_PARAMS : (technique?.params ?? []);
  return {
    id: typeof raw.id === 'string' ? raw.id : createLayerId(),
    techniqueId: raw.techniqueId,
    name: typeof raw.name === 'string' ? raw.name : (technique?.name ?? 'Text'),
    visible: raw.visible !== false,
    opacity: typeof raw.opacity === 'number' ? Math.min(1, Math.max(0, raw.opacity)) : 1,
    blendMode: raw.blendMode && BLEND_MODES.has(raw.blendMode) ? raw.blendMode : 'normal',
    seed: typeof raw.seed === 'number' ? Math.floor(Math.abs(raw.seed)) : 1,
    seedLocked: raw.seedLocked === true,
    values: sanitise(schema, raw.values),
    bulk: raw.bulk && typeof raw.bulk === 'object' ? raw.bulk : {},
    collapsed: raw.collapsed === true,
  };
}

export interface LoadResult {
  doc: StudioDocument;
  /** Technique ids in the preset that this build does not know. */
  droppedLayers: string[];
}

export function deserialisePreset(raw: unknown): LoadResult {
  if (!raw || typeof raw !== 'object') throw new Error('Preset-Datei ist leer oder kein JSON-Objekt.');
  const file = raw as Partial<PresetFile>;
  if (file.format !== 'typo-studio-preset') throw new Error('Das ist keine Typo-Studio-Preset-Datei.');
  if (!file.doc || typeof file.doc !== 'object') throw new Error('Preset enthält kein Dokument.');

  const d = file.doc as Partial<StudioDocument>;
  const droppedLayers: string[] = [];

  const layers: Layer[] = [];
  for (const rawLayer of Array.isArray(d.layers) ? d.layers : []) {
    const layer = sanitiseLayer(rawLayer);
    if (layer) layers.push(layer);
    else if (rawLayer && typeof rawLayer.techniqueId === 'string') droppedLayers.push(rawLayer.techniqueId);
  }
  // A document always has a text layer; re-create it if the preset lost it.
  if (!layers.some((l) => l.techniqueId === TEXT_TECHNIQUE_ID)) {
    layers.unshift({
      id: createLayerId('text'),
      techniqueId: TEXT_TECHNIQUE_ID,
      name: 'Text',
      visible: true,
      opacity: 1,
      blendMode: 'normal',
      seed: 1,
      seedLocked: false,
      values: defaultValues(TEXT_LAYER_PARAMS),
      bulk: {},
      collapsed: false,
    });
  }

  const activeLayerId = layers.some((l) => l.id === d.activeLayerId)
    ? (d.activeLayerId as string)
    : layers[layers.length - 1].id;

  const doc: StudioDocument = {
    textValues: sanitise(TEXT_SCHEMA, d.textValues),
    textBulk: d.textBulk && typeof d.textBulk === 'object' ? d.textBulk : {},
    fontId: typeof d.fontId === 'string' ? d.fontId : 'anton',
    anchorX: d.anchorX === 'left' || d.anchorX === 'right' ? d.anchorX : 'center',
    anchorY: d.anchorY === 'top' || d.anchorY === 'bottom' ? d.anchorY : 'middle',
    canvas: {
      width: clampInt(d.canvas?.width, 128, 8000, 1080),
      height: clampInt(d.canvas?.height, 128, 8000, 1350),
      exportScale: clampInt(d.canvas?.exportScale, 1, 8, 2),
    },
    background: sanitise(BACKGROUND_PARAMS, d.background),
    postFx: normalisePostChain(d.postFx),
    seed: typeof d.seed === 'number' ? Math.floor(Math.abs(d.seed)) : 1,
    seedLocked: d.seedLocked === true,
    layers,
    activeLayerId,
  };

  // Font-specific axis values are not in TEXT_SCHEMA; carry them across untouched and
  // let the loader clamp them once the font's `fvar` table is known.
  for (const [key, value] of Object.entries(d.textValues ?? {})) {
    if (key.startsWith('axis:') && typeof value === 'number') doc.textValues[key] = value;
  }

  return { doc, droppedLayers };
}

function clampInt(v: unknown, lo: number, hi: number, fallback: number): number {
  const n = typeof v === 'number' ? Math.round(v) : NaN;
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fallback;
}

// ── Share links ──────────────────────────────────────────────────────────────

const SHARE_KEY = 's';

/**
 * Packs a document into a URL fragment.
 *
 * The fragment is used rather than a query parameter so the state never reaches a
 * server, and LZ-String's URI-safe encoding avoids a second base64 round trip. A
 * typical document compresses to 1–2 kB, well inside what browsers accept.
 */
export function documentToShareUrl(doc: StudioDocument, baseUrl: string): string {
  const payload = compressToEncodedURIComponent(JSON.stringify(serialisePreset(doc, 'Geteilt')));
  const url = new URL(baseUrl);
  url.hash = `${SHARE_KEY}=${payload}`;
  return url.toString();
}

/** Reads a document out of the current URL fragment, if there is one. */
export function documentFromLocation(hash: string): LoadResult | null {
  const match = hash.replace(/^#/, '').match(new RegExp(`(?:^|&)${SHARE_KEY}=([^&]+)`));
  if (!match) return null;
  try {
    const json = decompressFromEncodedURIComponent(match[1]);
    if (!json) return null;
    return deserialisePreset(JSON.parse(json));
  } catch {
    return null;
  }
}
